import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  LinearProgress,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Storage as StorageIcon,
  Public as PublicIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Feedback as FeedbackIcon,
  Campaign as CampaignIcon,
  Article as ArticleIcon,
  AttachMoney as MoneyIcon,
  TrendingUp,
  TrendingDown,
  ArrowForward,
  HMobiledata as StatusIcon,
  Security as SecurityIcon,
  Devices as DevicesIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import DOMPurify from "dompurify";

import api from "../../utilities/api";
import { CustomCard } from "../../components/CustomCard";
import { UserLocationMap } from "../../components/UserLocationMap";
import BarChart from "../../components/ui/BarChart";
import BarChart2 from "../../components/ui/BarChart2";
import PieChart from "../../components/ui/PieChart";
import DropdownFilter from "../../components/ui/DropdownFilter";

// New Components
import RegistrationAnalytics from "./components/UserAnalytics";
import LocationAnalytics from "./components/LocationAnalytics";
import AuthAnalytics from "./components/AuthAnalytics";
import StorageAnalytics from "./components/StorageAnalytics";
import ContentAnalytics from "./components/ContentAnalytics";
import FinanceAnalytics from "./components/FinanceAnalytics";

// --- Helpers ---

const extractData = (res) => {
  if (!res) return null;
  if (res.data?.data?.rows) return res.data.data.rows;
  if (res.data?.data) return res.data.data;
  if (res.data?.rows) return res.data.rows;
  if (res.data) return res.data;
  return null;
};

// --- Sub-Components ---

const WidgetHeader = ({ title, icon: Icon, action }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {Icon && <Icon sx={{ color: theme.palette.text.secondary }} />}
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
};

const StorageWidget = ({ data = [] }) => {
  const theme = useTheme();
  const totalSize = 100; // GB Mock
  const safeData = Array.isArray(data) ? data : [];

  const processedData = safeData.map((app, index) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.info.main,
    ];
    return {
      ...app,
      size:
        app.size ||
        (app.sizeBytes
          ? parseFloat((app.sizeBytes / (1024 * 1024 * 1024)).toFixed(2))
          : 0),
      color: app.color || colors[index % colors.length],
    };
  });

  const totalUsed = processedData.reduce((a, b) => a + b.size, 0).toFixed(2);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Total Used
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {totalUsed} GB{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            / {totalSize} GB
          </Typography>
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: 12,
          bgcolor: theme.palette.action.hover,
          borderRadius: 6,
          display: "flex",
          overflow: "hidden",
          mb: 3,
        }}
      >
        {processedData.map((app, index) => (
          <Box
            key={index}
            sx={{
              width: `${(app.size / totalSize) * 100}%`,
              bgcolor: app.color,
            }}
          />
        ))}
      </Box>
      <Grid container spacing={2}>
        {processedData.slice(0, 6).map((app, index) => (
          <Grid item xs={6} sm={4} key={index}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: app.color,
                }}
              />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {app.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {app.size} GB
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const FeedbackList = ({ data = [] }) => {
  const theme = useTheme();
  if (!data || data.length === 0)
    return (
      <Typography color="text.secondary" align="center" py={2}>
        No new feedback
      </Typography>
    );

  return (
    <List disablePadding>
      {data.slice(0, 5).map((item, index) => (
        <React.Fragment key={index}>
          <ListItem alignItems="flex-start" disableGutters sx={{ py: 1 }}>
            <ListItemAvatar>
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                }}
              >
                {item.userEmail ? (
                  item.userEmail[0].toUpperCase()
                ) : (
                  <PersonIcon />
                )}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    noWrap
                    sx={{ maxWidth: "70%" }}
                  >
                    {item.userEmail || "Anonymous"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              }
              secondary={
                <Typography variant="body2" color="text.secondary" noWrap>
                  {item.content || item.message}
                </Typography>
              }
            />
          </ListItem>
          {index < data.length - 1 && (
            <Divider variant="inset" component="li" />
          )}
        </React.Fragment>
      ))}
    </List>
  );
};

const AnnouncementList = ({ data = [] }) => {
  if (!data || data.length === 0)
    return (
      <Typography color="text.secondary" align="center" py={2}>
        No active announcements
      </Typography>
    );

  return (
    <List disablePadding>
      {data.slice(0, 4).map((item, index) => (
        <ListItem key={index} disableGutters sx={{ py: 0 }}>
          <ListItemText
            primary={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2" fontWeight="medium">
                  {item.name || item.title}
                </Typography>
                <Chip
                  label={item.pageName || "Info"}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            }
            secondary={
              <Box display="flex" flexDirection="column">
                <Typography variant="body2" color="text.secondary" noWrap>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        item.text ||
                          "text will appear here..."
                      ),
                    }}
                  />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(item.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

const TopCountriesList = ({ data = [] }) => {
  if (!data || data.length === 0)
    return (
      <Typography color="text.secondary" align="center" py={2}>
        No data available
      </Typography>
    );

  return (
    <List disablePadding>
      {data.slice(0, 5).map((item, index) => (
        <ListItem key={index} disableGutters sx={{ py: 1 }}>
          <ListItemText
            primary={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2" fontWeight="medium">
                  {item.name || "Unknown"}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.count}
                </Typography>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const { mode } = theme.palette;

  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState("This Year");

  // Data States
  const [cardData, setCardData] = useState([]);
  const [revenueChart, setRevenueChart] = useState({
    series: [],
    categories: [],
  });
  const [userGrowthChart, setUserGrowthChart] = useState({
    series: [],
    categories: [],
  });
  const [subPieChart, setSubPieChart] = useState({
    series: [],
    labels: [],
    colors: [],
  });
  const [statusPieChart, setStatusPieChart] = useState({
    series: [],
    labels: [],
    colors: [],
  });
  const [authPieChart, setAuthPieChart] = useState({
    series: [],
    labels: [],
    colors: [],
  });
  const [deviceChart, setDeviceChart] = useState({
    series: [],
    categories: [],
  });
  const [storageData, setStorageData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [productStats, setProductStats] = useState([]);

  // New Analytics States
  const [hourlyChart, setHourlyChart] = useState({
    series: [],
    categories: [],
  });
  const [loginMethodChart, setLoginMethodChart] = useState({
    series: [],
    labels: [],
    colors: [],
  });
  const [topCountries, setTopCountries] = useState([]);

  // New Full Dashboard Analytics State
  const [dashboardAnalytics, setDashboardAnalytics] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const promises = [
          api.get("/api/subscriptions/analytics").catch((e) => ({ error: e })),
          api.get("/api/user/users/analytics").catch((e) => ({ error: e })),
          api.get("/api/adminfeedback?limit=5").catch((e) => ({ error: e })),
          api.get("/api/location/overview").catch((e) => ({ error: e })),
          api.get("/announcements?limit=5").catch((e) => ({ error: e })),
          api
            .get("/api/user/users-track/analytics")
            .catch((e) => ({ error: e })),
          api.get("/api/analytics/dashboard").catch((e) => ({ error: e })),
        ];

        const [
          subAnalyticsRes,
          userAnalyticsRes,
          feedbackRes,
          locationRes,
          announcementRes,
          userTrackRes,
          dashboardRes,
        ] = await Promise.all(promises);

        const userData = extractData(userAnalyticsRes);
        const feedbackData = extractData(feedbackRes);

        // --- New Analytics Data ---
        const newAnalyticsData = extractData(dashboardRes);
        if (newAnalyticsData) {
          setDashboardAnalytics(newAnalyticsData);
        }

        // --- Cards ---
        // Using newAnalyticsData for consistent single-source-of-truth if available, else fallback
        const da = newAnalyticsData;
        const totalSubs = da?.finance?.activeSubscriptions || 0;
        const totalUsers = da?.user?.total || 0;
        const totalStorage = da?.storage?.totalBytes
          ? (da.storage.totalBytes / (1024 * 1024 * 1024)).toFixed(1)
          : 0;
        const newUsersToday = da?.user?.new?.today || 0;
        const activeOffers = da?.content?.counts?.offers || 0;

        setCardData([
          {
            title: "Total Users",
            value: totalUsers.toLocaleString(),
            type: "increase",
            percentage: "2.4",
          },
          {
            title: "New Registrations Today",
            value: newUsersToday.toLocaleString(),
            type: "increase",
            percentage: "5.0",
          },
          {
            title: "Active Subscriptions",
            value: totalSubs.toLocaleString(),
            type: "increase",
            percentage: "3.1",
          },
          {
            title: "Total Storage Used",
            value: `${totalStorage} GB`,
            type: "neutral", // storage usually goes up
            percentage: "1.2",
          },
          {
            title: "Active Offers",
            value: activeOffers.toLocaleString(),
            type: "decrease",
            percentage: "0",
          },
        ]);

        // --- Charts ---
        // Revenue
        // if (transData?.monthlyStats) {
        //     const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        //     const data = new Array(12).fill(0);
        //     transData.monthlyStats.forEach(stat => {
        //         const d = new Date(stat.date);
        //         if (!isNaN(d.getTime())) data[d.getMonth()] = stat.revenue;
        //     });
        //     setRevenueChart({ series: [{ name: "Revenue", data }], categories: months });
        // }

        // User Growth
        if (userData?.series?.createdMonthly) {
          const currentYear = new Date().getFullYear();
          const thisYearData = new Array(12).fill(0);
          const lastYearData = new Array(12).fill(0);

          // userData.series.createdMonthly contains { year, month, count }
          userData.series.createdMonthly.forEach((item) => {
            if (item.year === currentYear)
              thisYearData[item.month - 1] = item.count;
            else if (item.year === currentYear - 1)
              lastYearData[item.month - 1] = item.count;
          });
          setUserGrowthChart({
            series: [
              { name: "Last Year", data: lastYearData },
              { name: "This Year", data: thisYearData },
            ],
            categories: [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ],
          });
        } else if (userData?.createdMonthly) {
          // Fallback for flat structure
          const currentYear = new Date().getFullYear();
          const thisYearData = new Array(12).fill(0);
          const lastYearData = new Array(12).fill(0);
          userData.createdMonthly.forEach((item) => {
            if (item.year === currentYear)
              thisYearData[item.month - 1] = item.count;
            else if (item.year === currentYear - 1)
              lastYearData[item.month - 1] = item.count;
          });
          setUserGrowthChart({
            series: [
              { name: "Last Year", data: lastYearData },
              { name: "This Year", data: thisYearData },
            ],
            categories: [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ],
          });
        }

        // Sub Products
        // const subProdData = extractData(subProductsRes);
        // if (Array.isArray(subProdData)) {
        //     const labels = [];
        //     const series = [];
        //     const colors = ["#00bcd4", "#ff1744", "#00e676", "#2979ff", "#ffd600", "#e040fb"];
        //     subProdData.forEach(p => {
        //         labels.push(p.productName || p.name || "Unknown");
        //         series.push(parseFloat(p.percentage) || 0);
        //     });
        //     setSubPieChart({ series, labels, colors: colors.slice(0, series.length) });
        // }

        // Auth Provider Stats (New)
        if (userData?.authProvider) {
          const providers = userData.authProvider;
          const labels = ["Google", "Facebook", "Email", "Other"];
          const series = [
            providers.google || 0,
            providers.facebook || 0,
            providers.auth || 0, // Assuming 'auth' means standard email/password
            0, // Placeholder for others if needed
          ];
          // Filter out zero values
          const finalLabels = [];
          const finalSeries = [];
          const finalColors = [];
          const colors = ["#DB4437", "#4267B2", "#0F9D58", "#F4B400"];

          series.forEach((val, idx) => {
            if (val > 0) {
              finalLabels.push(labels[idx]);
              finalSeries.push(val);
              finalColors.push(colors[idx]);
            }
          });

          setAuthPieChart({
            series: finalSeries,
            labels: finalLabels,
            colors: finalColors,
          });
        }

        // Login Analytics (Status & Apps)
        const trackData = extractData(userTrackRes);
        if (trackData) {
          // Status
          const active = trackData.sessions?.active || trackData.active || 0;
          const total = trackData.total || 0;
          const inactive = Math.max(0, total - active);
          setStatusPieChart({
            series: [active, inactive],
            labels: ["Logged In", "Logged Out"],
            colors: ["#00e676", "#7c4dff"],
          });

          // Top Apps
          if (trackData.loginApp && Array.isArray(trackData.loginApp)) {
            const appLabels = trackData.loginApp.slice(0, 5).map((a) => a.name);
            const appCounts = trackData.loginApp
              .slice(0, 5)
              .map((a) => a.count);
            setDeviceChart({
              series: [{ name: "Logins", data: appCounts }],
              categories: appLabels,
            });
          }

          // Hourly Activity
          if (
            trackData.series?.byHour &&
            Array.isArray(trackData.series.byHour)
          ) {
            const hours = trackData.series.byHour.map((h) => h.hour);
            const counts = trackData.series.byHour.map((h) => h.count);
            const categories = hours.map((h) => {
              const ampm = h >= 12 ? "PM" : "AM";
              const hour12 = h % 12 || 12;
              return `${hour12} ${ampm}`;
            });
            setHourlyChart({
              series: [{ name: "Logins", data: counts }],
              categories,
            });
          }

          // Login Methods
          if (trackData.loginMethod) {
            const methods = trackData.loginMethod;
            const labels = ["Email", "Google", "Facebook", "Code", "Unknown"];
            const series = [
              methods.password || 0,
              methods.google || 0,
              methods.facebook || 0,
              methods.code || 0,
              methods.unknown || 0,
            ];
            const finalLabels = [];
            const finalSeries = [];
            const finalColors = [];
            const colors = [
              "#2196f3",
              "#DB4437",
              "#4267B2",
              "#ff9800",
              "#9e9e9e",
            ];

            series.forEach((val, idx) => {
              if (val > 0) {
                finalLabels.push(labels[idx]);
                finalSeries.push(val);
                finalColors.push(colors[idx]);
              }
            });
            setLoginMethodChart({
              series: finalSeries,
              labels: finalLabels,
              colors: finalColors,
            });
          }

          // Top Countries
          if (trackData.topCountries && Array.isArray(trackData.topCountries)) {
            setTopCountries(trackData.topCountries);
          }
        }

        // Lists
        setLocationData(locationRes?.data?.data?.countryData || []);
        setFeedbacks(Array.isArray(feedbackData) ? feedbackData : []);
        setAnnouncements(
          Array.isArray(extractData(announcementRes))
            ? extractData(announcementRes)
            : []
        );
      } catch (error) {
        console.error("Dashboard Fetch Error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 2, px: 0 }}>
      {/* 1. Key Metrics Cards */}
      <Box sx={{ mb: 4 }}>
        <CustomCard data={cardData} mode={mode} length={cardData.length} />
      </Box>

      <Grid container spacing={2}>
        {/* 1. First Line: User Growth & Global User Distribution */}
        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${theme.palette.divider}`,
              height: "100%",
            }}
          >
            <WidgetHeader title="User Growth" icon={TrendingUp} />
            <Box sx={{ flexGrow: 1, minHeight: 100 }}>
              <BarChart2
                series={userGrowthChart.series}
                categories={userGrowthChart.categories}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <WidgetHeader title="Global User Distribution" icon={PublicIcon} />
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <UserLocationMap data={locationData} />
            </Box>
          </Box>
        </Grid>

        {/* 2. Second Line: Auth Providers, Login Methods, Top Countries */}
        <Grid item xs={12} md={4} lg={4}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <WidgetHeader title="Auth Providers" icon={SecurityIcon} />
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                minHeight: 100,
              }}
            >
              {authPieChart.series.length > 0 ? (
                <PieChart
                  series={authPieChart.series}
                  labels={authPieChart.labels}
                  colors={authPieChart.colors}
                />
              ) : (
                <Typography
                  color="text.secondary"
                  align="center"
                  width="100%"
                >
                  No data available
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={4} lg={4}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <WidgetHeader title="Login Methods" icon={SecurityIcon} />
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                minHeight: 100,
              }}
            >
              <PieChart
                series={loginMethodChart.series}
                labels={loginMethodChart.labels}
                colors={loginMethodChart.colors}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={4} lg={4}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <WidgetHeader
              title="Top Countries (Logins)"
              icon={PublicIcon}
            />
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              <TopCountriesList data={topCountries} />
            </Box>
          </Box>
        </Grid>

        {/* 3. Third Line: Login Activity by Hour & Top Login Apps */}
        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <WidgetHeader
              title="Login Activity by Hour"
              icon={AccessTimeIcon}
            />
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <BarChart2
                series={hourlyChart.series}
                categories={hourlyChart.categories}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <WidgetHeader title="Top Login Apps" icon={DevicesIcon} />
            <Box sx={{ flexGrow: 1, minHeight: 200 }}>
              <BarChart
                series={deviceChart.series}
                categories={deviceChart.categories}
                color={theme.palette.info.main}
              />
            </Box>
          </Box>
        </Grid>



        {/* 1. User & Location */}
        <Grid item xs={12} lg={6}>
          <RegistrationAnalytics data={dashboardAnalytics?.user} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <LocationAnalytics data={dashboardAnalytics?.location} />
        </Grid>

        {/* 2. Auth & Storage */}
        <Grid item xs={12} lg={6}>
          <AuthAnalytics data={dashboardAnalytics?.auth} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <StorageAnalytics data={dashboardAnalytics?.storage} />
        </Grid>

        {/* 3. Content & Finance */}
        <Grid item xs={12} md={6} lg={6}>
          <ContentAnalytics data={dashboardAnalytics?.content} />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <FinanceAnalytics data={dashboardAnalytics?.finance} />
        </Grid>

        {/* 4. Lists (Existing) */}
        <Grid item xs={12} md={6} lg={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <WidgetHeader title="Recent Feedback" icon={FeedbackIcon} />
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              <FeedbackList data={feedbacks} />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              height:"100%"
            }}
          >
            <WidgetHeader title="Announcements" icon={CampaignIcon} />
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              <AnnouncementList data={announcements} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
